import { Button, Col, Row } from 'react-bootstrap'
import { useMutation } from 'react-query'
import { get } from 'lodash-es'
import { IoWarningOutline } from 'react-icons/io5'
import { useHistory } from 'react-router-dom'
import { useBreakpoint } from '../../../hooks'

import useContainerContext from '../../containerContext'
import { DocumentHeader, HeaderButton, PDFDropdownButton, VehicleItem } from '../../shared'
import ModalPaymentInfo from '../../paymentInfo'
import ModalReceipt from '../../paymentInfo/receipt'
import ModalPhotoList from '../../ModalPhotoList'
import useAppContext from '../../app'
import { useApi, useLoading } from '../../../hooks'
import { numberFormatCurrency } from '../../../utils/helpers'
import { discountPercent, taxPercent, taxTotal } from '../../../utils/invoice'
import toast from '../../toast'

const InvoiceDetail = () => {
	const history = useHistory();
	const { store: { isLog } } = useAppContext();
	const { data, state, showPhotoModal, closeModalPhoto, toPayment } = useContainerContext();
	const { showModalPhoto, vehicle, initialSlide, isPayment, isReceipt } = state;
	const { company, vehicles = [], client: { _id: client = {} } } = data;
	const { BREAKPOINT, screenBiggerThan } = useBreakpoint();
	const isBiggerThanMD = screenBiggerThan(BREAKPOINT.MD);
	let isShowPdf = true;
	let isShowLinkToPay = true;
	let hasDisclosure = false;
	let showScans = true;
	let lang;
	let dualInvoicing = false;
	let defaultVersion = 'discount';

	if (!isLog) {
		const token = localStorage['access_token'];
		const payload = token ? JSON.parse(window.atob(token.split('.')[1])) : null
		isShowPdf = payload?.pdf;
		isShowLinkToPay = payload?.linkToPay;
		hasDisclosure = payload?.hasDisclosure;
		showScans = payload?.scans !== false;
		lang = payload?.language;
		dualInvoicing = payload?.dualInvoicing || data?.dualInvoicing || false;
		defaultVersion = payload?.defaultVersion || data?.defaultVersion || 'discount';
	} else {
		dualInvoicing = data?.dualInvoicing || false;
		defaultVersion = data?.defaultVersion || 'discount';
	}

	const companyProps = (() => {
		if (client.group && client.group.companyOverride && client.group.companyOverride.active) return client.group.companyOverride;
		return company;
	})();
	const companyLogo = companyProps.logo || company?.logo || null;

	const { discountAmount = 0 } = data;
	const vehiclesSubtotal = vehicles.reduce((sum, v) => sum + (v.total || 0), 0);
	// Figures shown beside TOTAL in the header; each is dropped when zero.
	const summaryColumns = [];
	if (vehiclesSubtotal > 0) {
		summaryColumns.push({
			label: 'Subtotal',
			value: numberFormatCurrency(data.currency, vehiclesSubtotal),
		});
	}
	if (data.discountAmount > 0) {
		summaryColumns.push({
			label: `Discount (${discountPercent(data)})`,
			value: numberFormatCurrency(data.currency, data.discountAmount),
		});
	}
	if (data.taxLabor || data.taxParts) {
		summaryColumns.push({
			label: `Tax (${taxPercent(data)})`,
			value: taxTotal(data),
		});
	}
	const isDualInvoicingEnabled = dualInvoicing && discountAmount > 0;

	const { pdfService } = useApi();
	const { isLoading, mutate: downloadPdf } = useMutation(
		params => pdfService.invoicePDF(params, false),
		{ onError: error => toast.error({ title: 'View PDF errors', body: error.message }) }
	);
	useLoading(isLoading);

	const paymentStatus = data?.payment?.paid;
	const paymentAmount = get(data, 'payment.amount', 0.00);
	const balance = (data.total || 0) - paymentAmount === 0 ? 0.00 : (data.total || 0) - paymentAmount;
	const stripe = data?.company?.stripe;
	const showPayButton = isShowLinkToPay && stripe && stripe.userID && paymentStatus !== 'paid';

	const getStatusBadge = () => {
		const badges = [];
		if (paymentStatus === 'paid') {
			badges.push(
				<Button
					key="paid"
					variant='success'
					className='fs-small text-white fw-bold py-1 px-3 rounded-pill d-flex align-items-center'
					style={{ pointerEvents: 'none' }}
				>
					Paid
				</Button>
			);
		}
		if (paymentStatus === 'partial') {
			badges.push(
				<Button
					key="partial"
					className='fs-small text-white fw-bold py-1 px-3 rounded-pill d-flex align-items-center'
					style={{ pointerEvents: 'none', backgroundColor: '#E87B04', borderColor: '#E87B04' }}
				>
					Partial
				</Button>
			);
		}
		if (paymentStatus === 'unpaid') {
			badges.push(
				<Button
					key="unpaid"
					className='fs-small text-white fw-bold py-2 px-3 rounded-pill d-flex align-items-center'
					style={{ pointerEvents: 'none', backgroundColor: '#939DA5', border: 'none' }}
				>
					Unpaid
				</Button>
			);
		}
		if (paymentStatus !== 'paid' && balance > 0) {
			badges.push(
				<Button
					key="balance"
					variant='warning'
					className='fs-small text-white fw-bold py-1 px-3 rounded-pill d-flex align-items-center'
					style={{ pointerEvents: 'none' }}
				>
					<IoWarningOutline size={16} className='me-1' />
					Balance Due {numberFormatCurrency(data.currency, balance)}
				</Button>
			);
		}
		return badges.length > 0 ? <>{badges}</> : null;
	};

	const getPdfButtons = () => {
		const btns = [];
		if (showPayButton) {
			btns.push(
				<HeaderButton key="pay" variant="outline" onClick={toPayment}>
					PAY INVOICE
				</HeaderButton>
			);
		}
		if (isShowPdf) {
			if (isDualInvoicingEnabled) {
				btns.push(
					<PDFDropdownButton
						key="pdf"
						onViewPdf={(withoutDiscount) => downloadPdf({ recordId: data._id, hasDisclosure, lang, withoutDiscount })}
					/>
				);
			} else {
				btns.push(
					<HeaderButton key="pdf" variant="primary" onClick={() => downloadPdf({ recordId: data._id, hasDisclosure, lang })}>
						VIEW PDF
					</HeaderButton>
				);
			}
		}
		return btns.length > 0 ? <>{btns}</> : null;
	};

	return (
		<div className='my-5 px-sm-3'>
			<Row>
				<Col md={12}>
					<div className='pt-4'>
						<DocumentHeader
							documentType="Invoice"
							documentNumber={data.number}
							purchaseOrder={data.purchaseOrder}
							dateCreated={data.dateCreated}
							createdBy={data.createdBy?.name}
							total={data.total || 0}
							summaryColumns={summaryColumns}
							currency={data.currency}
							client={client}
							company={companyProps}
							companyLogo={companyLogo}
							onBack={() => window.history.back()}
							statusBadge={getStatusBadge()}
							buttons={getPdfButtons()}
						/>

						<div className='mt-3'>
							{vehicles.map((vehicle, index) => (
								<VehicleItem
									key={vehicle._id || index}
									index={index}
									vehicle={vehicle}
									record={data}
									onShowPhoto={showPhotoModal}
									showScans={showScans}
								/>
							))}
						</div>
					</div>
				</Col>
			</Row>

			{isPayment && <ModalPaymentInfo data={data} />}
			{isReceipt && <ModalReceipt />}
			{showModalPhoto && <ModalPhotoList vehicle={vehicle} initialSlide={initialSlide} onClose={closeModalPhoto} isInvoice={true} />}
		</div>
	)
}
export default InvoiceDetail
